import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { rows, type ID, type OrgUnit, type PermissionDef, type Role } from '@hrms/shared';
import { api } from '../../../lib/api';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { Alert, Badge, Button, Card, Checkbox, Columns, ConfirmDialog, EmptyState, IconButton, ListItem, Loading, Modal, PageHeader, Row, Screen, Stack, Text, TextField, useTheme, type Option } from '../../../ui';
import { AutoGrid, clone } from '../../payroll/components/common';
import { MultiSelect } from '../components/MultiSelect';

type Scope = NonNullable<Role['scope']>;
const opt = (list: OrgUnit[]): Option<ID>[] => list.map((u) => ({ value: u.id, label: u.code ? `${u.name} (${u.code})` : u.name }));

export function RolesScreen() {
  const { colors } = useTheme();
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.admin.roles.list() });
  const perms = useQuery({ queryKey: ['permissions'], queryFn: () => api.admin.permissions() });
  const le = useQuery({ queryKey: ['org', 'legalEntities', 'all'], queryFn: () => api.org.legalEntities.list({ per_page: 500 }) });
  const loc = useQuery({ queryKey: ['org', 'locations', 'all'], queryFn: () => api.org.locations.list({ per_page: 500 }) });
  const dep = useQuery({ queryKey: ['org', 'departments', 'all'], queryFn: () => api.org.departments.list({ per_page: 500 }) });
  const list = rows(roles.data);
  const [selId, setSelId] = useState<ID | 'new' | null>(null);
  const [r, setR] = useState<Partial<Role> | null>(null);
  const [cloneName, setCloneName] = useState<string | null>(null);
  const [del, setDel] = useState(false);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (selId === null && list.length) setSelId(list[0].id);
  }, [list, selId]);
  useEffect(() => {
    if (selId === 'new') setR({ name: '', permissions: [], scope: null });
    else {
      const x = list.find((y) => y.id === selId);
      setR(x ? clone(x) : null);
    }
    setErrs({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId, roles.data]);

  const groups = useMemo(() => {
    const g = new Map<string, PermissionDef[]>();
    for (const p of perms.data ?? []) {
      if (filter && !`${p.label} ${p.key} ${p.group}`.toLowerCase().includes(filter.toLowerCase())) continue;
      g.set(p.group, [...(g.get(p.group) ?? []), p]);
    }
    return [...g.entries()];
  }, [perms.data, filter]);

  const isSuper = r?.slug === 'super_admin';
  const permLocked = isSuper;
  const nameLocked = !!r?.is_system;

  const save = useMut(
    () => {
      const body: Partial<Role> = { name: r!.name, scope: r!.scope ?? null };
      if (!permLocked) body.permissions = r!.permissions ?? [];
      return r?.id ? api.admin.roles.update(r.id, body) : api.admin.roles.create(body);
    },
    { invalidate: [['roles']], success: 'Role saved', onSuccess: (x) => setSelId(x.id), onError: (e) => setErrs(fieldErrors(e)) },
  );
  const cloneM = useMut(() => api.admin.roles.clone(r!.id!, cloneName!), {
    invalidate: [['roles']],
    success: 'Role cloned',
    onSuccess: (x) => {
      setCloneName(null);
      setSelId(x.id);
    },
  });
  const remove = useMut(() => api.admin.roles.remove(r!.id!), {
    invalidate: [['roles']],
    success: 'Role deleted',
    onSuccess: () => {
      setDel(false);
      setSelId(null);
    },
  });

  const granted = new Set(isSuper ? (perms.data ?? []).map((p) => p.key) : r?.permissions ?? []);
  const toggle = (k: string, on: boolean) => setR({ ...r, permissions: on ? [...(r?.permissions ?? []), k] : (r?.permissions ?? []).filter((x) => x !== k) });
  const setGroup = (ps: PermissionDef[], on: boolean) =>
    setR({ ...r, permissions: on ? Array.from(new Set([...(r?.permissions ?? []), ...ps.map((p) => p.key)])) : (r?.permissions ?? []).filter((k) => !ps.some((p) => p.key === k)) });
  const scope: Scope = r?.scope ?? {};
  const setScope = (patch: Scope) => {
    const n = { ...scope, ...patch };
    const empty = !n.legal_entity_ids?.length && !n.location_ids?.length && !n.department_ids?.length;
    setR({ ...r, scope: empty ? null : n });
  };

  const listCard = (
    <Card title="Roles" flush>
      {roles.isLoading ? (
        <Loading />
      ) : (
        <View testID="roles-list">
          {list.map((x, i) => (
            <ListItem
              key={x.id}
              title={x.name}
              subtitle={`${x.users_count ?? 0} user${x.users_count === 1 ? '' : 's'} · ${x.slug === 'super_admin' ? 'all' : x.permissions.length} permissions`}
              highlight={x.id === selId}
              divider={i < list.length - 1}
              right={x.is_system ? <Badge>System</Badge> : undefined}
              onPress={() => setSelId(x.id)}
            />
          ))}
          {!list.length && <EmptyState icon="shield" title="No roles" />}
        </View>
      )}
    </Card>
  );

  const editor = !r ? (
    <Card>
      <EmptyState icon="shield" title="Select a role" />
    </Card>
  ) : (
    <Stack>
      <Card
        title={r.id ? r.name : 'New role'}
        subtitle={r.slug}
        actions={
          <>
            {!!r.id && <Button icon="layers" title="Clone" onPress={() => setCloneName(`${r.name} (copy)`)} testID="clone-role" />}
            {!!r.id && !r.is_system && <IconButton icon="trash" label="Delete role" onPress={() => setDel(true)} />}
            <Button variant="primary" title="Save" loading={save.isPending} onPress={() => save.mutate(undefined)} testID="save-role" />
          </>
        }
      >
        <Stack gap={12}>
          <TextField label="Role name" required value={r.name ?? ''} disabled={nameLocked} error={errs.name} onChangeText={(v) => setR({ ...r, name: v })} testID="role-name" />
          {isSuper && <Alert tone="info">The Super Admin role always has every permission. Clone it to create a restricted admin.</Alert>}
          {!isSuper && r.is_system && <Text variant="small">System role: the name is fixed and it can't be deleted, but permissions and scope can be adjusted. Clone it to create a variant.</Text>}
          {!!r.id && <Text variant="small">{`${r.users_count ?? 0} user(s) have this role.`}</Text>}
        </Stack>
      </Card>
      <Card title="Data scope" subtitle="Restrict what users with this role can see. Leave empty for the whole organisation.">
        <AutoGrid min={220}>
          <MultiSelect label="Legal entities" value={scope.legal_entity_ids ?? []} options={opt(rows(le.data))} onChange={(v) => setScope({ legal_entity_ids: v })} placeholder="All legal entities" testID="scope-legal" />
          <MultiSelect label="Locations" value={scope.location_ids ?? []} options={opt(rows(loc.data))} onChange={(v) => setScope({ location_ids: v })} placeholder="All locations" testID="scope-locations" />
          <MultiSelect label="Departments" value={scope.department_ids ?? []} options={opt(rows(dep.data))} onChange={(v) => setScope({ department_ids: v })} placeholder="All departments" testID="scope-departments" />
        </AutoGrid>
      </Card>
      <Card
        title="Permissions"
        subtitle={`${granted.size} of ${perms.data?.length ?? 0} granted`}
        actions={<TextField placeholder="Filter permissions…" value={filter} onChangeText={setFilter} accessibilityLabel="Filter permissions" containerStyle={{ width: 220 }} />}
      >
        {!!errs.permissions && <Alert tone="danger">{errs.permissions}</Alert>}
        {perms.isLoading ? (
          <Loading />
        ) : (
          <View testID="permission-matrix">
          <AutoGrid min={280}>
            {groups.map(([g, ps]) => {
              const all = ps.every((p) => granted.has(p.key));
              return (
                <View key={g} style={{ borderWidth: 1, borderColor: colors.border, borderRadius: 10, padding: 12, gap: 8 }}>
                  <Row justify="space-between">
                    <Text weight="semibold">{g}</Text>
                    <Checkbox label="All" value={all} disabled={permLocked} onChange={(v) => setGroup(ps, v)} />
                  </Row>
                  {ps.map((p) => (
                    <View key={p.key} style={{ gap: 2 }}>
                      <Checkbox label={p.label} value={granted.has(p.key)} disabled={permLocked} onChange={(v) => toggle(p.key, v)} />
                      <Text variant="mono" size={11} color={colors.faint} style={{ paddingLeft: 26 }}>
                        {p.key}
                      </Text>
                    </View>
                  ))}
                </View>
              );
            })}
          </AutoGrid>
          </View>
        )}
      </Card>
    </Stack>
  );

  return (
    <Screen refreshing={roles.isRefetching} onRefresh={() => roles.refetch()} testID="roles">
      <PageHeader title="Roles & permissions" subtitle="Roles bundle permissions and an optional data scope." actions={<Button variant="primary" icon="plus" title="New role" onPress={() => setSelId('new')} testID="new-role" />} />
      <Columns ratio={[1, 3]}>
        {listCard}
        {editor}
      </Columns>
      <Modal
        open={cloneName !== null}
        onClose={() => setCloneName(null)}
        title="Clone role"
        width={420}
        footer={
          <>
            <Button title="Cancel" onPress={() => setCloneName(null)} />
            <Button variant="primary" title="Clone" disabled={!cloneName} loading={cloneM.isPending} onPress={() => cloneM.mutate(undefined)} />
          </>
        }
      >
        <TextField label="New role name" value={cloneName ?? ''} onChangeText={setCloneName} />
      </Modal>
      <ConfirmDialog open={del} onClose={() => setDel(false)} onConfirm={() => remove.mutate(undefined)} loading={remove.isPending} title="Delete role?" message="Roles that still have users can't be deleted." danger confirmLabel="Delete" />
    </Screen>
  );
}
